import React, { useMemo } from "react";
import {
  Clock,
  Pencil,
  DollarSign,
  FileText,
  Power,
  ListChecks,
} from "lucide-react";

// ── Attribute → icon/color/label, matches what logChange() writes ──
const ATTRIBUTE_RULES = {
  title: { label: "Title updated", icon: Pencil, color: "#2563eb" },
  description: {
    label: "Description updated",
    icon: FileText,
    color: "#7c3aed",
  },
  vinted_desc: {
    label: "Vinted description updated",
    icon: FileText,
    color: "#0b9aa3",
  },
  price: { label: "Price updated", icon: DollarSign, color: "#16a34a" },
  status: { label: "Status updated", icon: Power, color: "#d97706" },
  list: { label: "Listed", icon: Power, color: "#16a34a" },
  delist: { label: "Delisted", icon: Power, color: "#dc2626" },
};

const getRule = (attribute) =>
  ATTRIBUTE_RULES[attribute] || {
    label: attribute || "Updated",
    icon: Pencil,
    color: "#64748b",
  };

const formatTimestamp = (value) => {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const formatDuration = (ms) => {
  if (ms <= 0) return "same time";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m later`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs}h ${rem}m later` : `${hrs}h later`;
};

const buildSentence = (log) => {
  const platformLabel =
    log.platform && log.platform !== "All" ? ` on ${log.platform}` : "";
  const who = log.changed_by_name || `user #${log.changed_by}`;
  const rule = getRule(log.attribute);

  if (log.previous_value === log.new_value || log.new_value === null) {
    return `${rule.label}${platformLabel} by ${who}`;
  }
  return `${rule.label}${platformLabel} by ${who} — "${log.previous_value ?? "—"}" to "${log.new_value ?? "—"}"`;
};

function TimelineRow({ log, isLast }) {
  const sentence = buildSentence(log);
  const idx = sentence.indexOf(
    log.changed_by_name || `user #${log.changed_by}`,
  );
  const actorLabel = log.changed_by_name || `user #${log.changed_by}`;
  const before = idx === -1 ? sentence : sentence.slice(0, idx);
  const after = idx === -1 ? "" : sentence.slice(idx + actorLabel.length);

  return (
    <div style={{ display: "flex", gap: 14 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 12,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#334155",
            marginTop: 20,
            flexShrink: 0,
          }}
        />
        {!isLast && (
          <span
            style={{ flex: 1, width: 1, background: "#cbd5e1", marginTop: 4 }}
          />
        )}
      </div>

      <div
        style={{
          flex: 1,
          background: "#fff",
          borderRadius: 10,
          padding: "14px 18px",
          marginBottom: 12,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <p style={{ margin: 0, fontSize: 14, color: "#334155" }}>
          <span style={{ color: "#94a3b8" }}>{"\u2022 "}</span>
          {before}
          {idx !== -1 && (
            <span style={{ color: "#2563eb", fontWeight: 600 }}>
              {actorLabel}
            </span>
          )}
          {after}
        </p>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: 12.5,
            color: "#94a3b8",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <Clock size={13} color="#ef4444" />
          {formatTimestamp(log.created_at)}
        </span>
      </div>
    </div>
  );
}

function SummaryRow({ item }) {
  const rule = getRule(item.attribute);
  const Icon = rule.icon;
  const who = item.changed_by_name || `user #${item.changed_by}`;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 10,
        padding: "14px 18px",
        marginBottom: 10,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: `${rule.color}1a`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={17} color={rule.color} />
      </span>

      <div style={{ flex: 1, minWidth: 200 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 13.5, color: "#0f172a" }}>
            {rule.label}
          </span>
          {item.platform && item.platform !== "All" && (
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              {item.platform}
            </span>
          )}
        </div>
        <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>
          by <span style={{ color: "#2563eb", fontWeight: 600 }}>{who}</span>
          {item.sincePrev !== null && (
            <span style={{ color: "#94a3b8" }}>
              {" "}
              · {formatDuration(item.sincePrev)}
            </span>
          )}
          {item.previous_value !== item.new_value &&
            item.new_value !== null && (
              <span style={{ color: "#94a3b8" }}>
                {" "}
                · {item.previous_value ?? "—"} → {item.new_value ?? "—"}
              </span>
            )}
        </p>
      </div>

      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: 12,
          color: "#94a3b8",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <Clock size={12} color="#ef4444" />
        {formatTimestamp(item.created_at)}
      </span>
    </div>
  );
}

// `logs`: array of rows exactly as returned by
// GET /api/update-products/logs/:product_id
// -> { id, attribute, previous_value, new_value, platform, changed_by, changed_by_name, created_at }
export default function ProductLogTimeline({ logs = [] }) {
  const enriched = useMemo(() => {
    const sorted = [...logs].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
    return sorted.map((log, i) => ({
      ...log,
      sincePrev:
        i === 0
          ? null
          : new Date(log.created_at) - new Date(sorted[i - 1].created_at),
    }));
  }, [logs]);

  if (enriched.length === 0) {
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "24px 28px",
          textAlign: "center",
        }}
      >
        <p style={{ color: "#94a3b8", fontSize: 14, margin: 0 }}>
          No changes logged for this product yet.
        </p>
      </div>
    );
  }

  const totalSpanMs =
    enriched.length > 1
      ? new Date(enriched[enriched.length - 1].created_at) -
        new Date(enriched[0].created_at)
      : 0;

  return (
    <div>
      {/* ── Activity timeline ── */}
      <div style={{ marginBottom: 28 }}>
        {enriched.map((log, i) => (
          <TimelineRow
            key={log.id}
            log={log}
            isLast={i === enriched.length - 1}
          />
        ))}
      </div>

      {/* ── Log summary ── */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <ListChecks size={16} color="#334155" />
          <h6
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Log Summary
          </h6>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            {enriched.length} change{enriched.length === 1 ? "" : "s"}
            {totalSpanMs > 0 &&
              ` · ${formatDuration(totalSpanMs).replace(" later", " total")}`}
          </span>
        </div>

        {enriched.map((item) => (
          <SummaryRow key={`summary-${item.id}`} item={item} />
        ))}
      </div>
    </div>
  );
}
